import { openModal, closeModal, esc, showModalErr } from '../ui/modal';
import { t } from '../ui/lang';
// Global source-IP whitelist editor for the remote-ADB listeners. The list is the
// only network guard on an open ADB endpoint (superadmin gate aside), so the empty
// state is spelled out as "deny all". Validation is enforced server-side too; this
// just gives fast feedback.
// Basic client-side sanity check (v4 or v6). The server is authoritative — it
// re-validates with net.isIP and rejects the whole PUT on any bad entry.
function looksLikeIp(ip) {
    const v4 = /^(\d{1,3})(\.\d{1,3}){3}$/;
    if (v4.test(ip))
        return ip.split('.').every((o) => Number(o) <= 255);
    return ip.includes(':') && /^[0-9a-fA-F:]+$/.test(ip);
}
export async function showAdbWhitelistModal() {
    let ips = [];
    try {
        const res = await fetch('/api/adb-proxy/whitelist');
        if (res.ok)
            ips = (await res.json()).ips;
    }
    catch { /* start from empty */ }
    const modal = openModal(`
        <h2 class="modal__title">${t('adbproxy.whitelist_title')}</h2>
        <p class="settings-hint">
            ${t('adbproxy.whitelist_hint')}
        </p>
        <div class="modal__field">
            <label>${t('adbproxy.add_ip')}</label>
            <div style="display:flex;gap:8px">
                <input class="settings-input awl-input" type="text" autocomplete="off" placeholder="${t('adbproxy.ip_placeholder')}" />
                <button type="button" class="modal__submit awl-add">${t('common.add')}</button>
            </div>
        </div>
        <div class="awl-list" style="margin:12px 0"></div>
        <div class="modal__error awl-error" style="display:none"></div>
        <div class="modal__actions">
            <button type="button" class="modal__cancel awl-cancel">${t('common.cancel')}</button>
            <button type="button" class="modal__submit awl-save">${t('common.save')}</button>
        </div>
    `);
    const input = modal.querySelector('.awl-input');
    const addBtn = modal.querySelector('.awl-add');
    const listEl = modal.querySelector('.awl-list');
    const errEl = modal.querySelector('.awl-error');
    const saveBtn = modal.querySelector('.awl-save');
    modal.querySelector('.awl-cancel').addEventListener('click', () => closeModal());
    function renderList() {
        listEl.innerHTML = ips.length === 0
            ? `<p class="settings-hint">${t('adbproxy.whitelist_empty')}</p>`
            : ips.map((ip, i) => `
                <div class="clicker-farm-row" style="cursor:default;padding:6px 10px">
                    <span style="flex:1"><code>${esc(ip)}</code></span>
                    <button type="button" class="tbl-btn awl-del" data-i="${i}" title="${t('common.remove')}">✕</button>
                </div>
            `).join('');
        listEl.querySelectorAll('.awl-del').forEach((b) => {
            b.addEventListener('click', () => {
                ips.splice(parseInt(b.dataset['i'] ?? '', 10), 1);
                renderList();
            });
        });
    }
    function addIp() {
        errEl.style.display = 'none';
        const ip = input.value.trim();
        if (!ip)
            return;
        if (!looksLikeIp(ip)) {
            showModalErr(errEl, t('adbproxy.invalid_ip', { ip }));
            return;
        }
        if (!ips.includes(ip))
            ips.push(ip);
        input.value = '';
        renderList();
        input.focus();
    }
    addBtn.addEventListener('click', addIp);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') {
        e.preventDefault();
        addIp();
    } });
    saveBtn.addEventListener('click', async () => {
        errEl.style.display = 'none';
        // Commit any IP typed but not yet added so it isn't silently lost on save.
        // Reuse the Add button's validation/normalization; block the save on invalid
        // input and surface the same feedback rather than saving without it.
        const pending = input.value.trim();
        if (pending) {
            if (!looksLikeIp(pending)) {
                showModalErr(errEl, t('adbproxy.invalid_ip', { ip: pending }));
                return;
            }
            if (!ips.includes(pending))
                ips.push(pending);
            input.value = '';
            renderList();
        }
        saveBtn.disabled = true;
        try {
            const res = await fetch('/api/adb-proxy/whitelist', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ips }),
            });
            if (!res.ok) {
                showModalErr(errEl, (await res.json().catch(() => ({}))).error ?? t('common.failed_to_save'));
                saveBtn.disabled = false;
                return;
            }
            closeModal();
        }
        catch {
            showModalErr(errEl, t('adbproxy.network_error'));
            saveBtn.disabled = false;
        }
    });
    renderList();
}
